import { toast } from 'sonner'
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'

function QuickBooksConfigModal({
  open,
  onOpenChange,
  integration,
  onComplete,
}: ConfigModalProps) {
  const handleDisconnect = async () => {
    if (!integration) return

    try {
      await trpcClient.integrations.deleteIntegration.mutate({
        id: integration.id,
      })
      toast.success('QuickBooks disconnected')
      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
      onOpenChange(false)
      onComplete()
    } catch (error) {
      toast.error(
        `Failed to disconnect: ${
          error instanceof Error ? error.message : 'unknown'
        }`
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IntegrationIcon type="QuickBooks" />
            QuickBooks
          </DialogTitle>
          <DialogDescription>
            {integration
              ? 'Connected via Intuit OAuth. Screenata can collect read-only accounting system, master data, transaction, and report evidence.'
              : 'Click Connect on the card to authorize Screenata to read QuickBooks Online evidence.'}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {integration && (
            <Button variant="destructive" onClick={handleDisconnect}>
              Disconnect
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const QuickBooksProvider: IntegrationProvider = {
  id: 'QuickBooks',
  name: 'QuickBooks',
  description:
    'Validate QuickBooks access and collect accounting system, master data, transaction, and report evidence',
  Icon: () => <IntegrationIcon type="QuickBooks" />,
  ConfigModal: QuickBooksConfigModal,

  handleConnect: async ({ redirectUrl, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish)

    try {
      const finalRedirect =
        redirectUrl ??
        `${import.meta.env.VITE_WEB_URL ?? window.location.origin}/auth-callback/QuickBooks`

      const data = await trpcClient.integrations.initQuickBooksAuth.mutate({
        redirectUrl: finalRedirect,
      })
      if (!data.authUrl) {
        toast.error('Failed to initialize QuickBooks OAuth')
        finish('error')
        return
      }

      openOAuthConnectPopup({
        authUrl: data.authUrl, windowName: 'quickbooks-auth', provider: 'QuickBooks',
        onSuccess: result => {
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
          toast.success('QuickBooks connected');
          onSuccess?.(result);
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: finish,
      })
    } catch (error) {
      toast.error(
        `Error initializing QuickBooks authentication: ${
          error instanceof Error ? error.message : 'unknown error'
        }`
      )
      finish('error')
    }
  },

  onConnectComplete: async ({
    code,
    state,
    realmId,
  }: {
    code: string
    state: string
    realmId?: string | null
  }) => {
    if (!realmId) {
      throw new Error('QuickBooks authorization did not return a realmId')
    }

    return await trpcClient.integrations.completeQuickBooksAuth.mutate({
      code,
      state,
      realmId,
    })
  },

  getFeatures: () => [
    {
      id: 'quickbooks_evidence',
      name: 'Accounting evidence',
      description:
        'Collect read-only QuickBooks system, master data, transaction, and report evidence',
      enabled: true,
    },
  ],

  syncData: async () => {},
}
